/**
 * The kinds of node an eval can be made of, and what a fresh one looks like.
 *
 * A node's shape on the server is fully determined by its instrument, its
 * typed output fields and its edges (graphSchema, TRO-412). `type` is a
 * palette hint. This file is the client's view of that: one entry per kind
 * a person can add, with the default config, output and edges a new node
 * starts with, and the accent it wears on the canvas.
 *
 * Icons were picked one by one (Nov 2026): the LLM judge wears the trace
 * tree's LLM atom, Filter is the app's one funnel, and checks are the seal.
 * Accents are theme tokens, so every kind has a light and a dark value.
 *
 * The two semantic instruments (bi_encoder, cross_encoder) are one kind
 * here, the Semantic check: which of them a node is follows from its check
 * (see ./semantic.js), never from a choice a person makes.
 */
import { Code2, Filter } from 'lucide-react';
import { PiAtomDuotone, PiMagnifyingGlassDuotone, PiUserCheckDuotone, PiSealCheckDuotone, PiXCircleDuotone } from 'react-icons/pi';
import { TbArrowsSplit2 } from 'react-icons/tb';
import { outcomesOf, isRange, topOf } from 'shared/decision/routing';
import { defaultsFor } from './semantic';

export const DEFAULT_PYTHON_SOURCE = [
  'def evaluate(t):',
  '    # t is the trace: t.run, t.spans, t.conversation, t.meta',
  '    # Return the value this node declares in Output —',
  '    # True/False for a boolean, a number, or one of the enum members.',
  "    return 'error' not in text(t.run.output).lower()",
].join('\n');

export const DEFAULT_JUDGE_PROMPT = [
  'You are grading one answer from an AI assistant.',
  '',
  'User asked:',
  '{{run.input}}',
  '',
  'Assistant answered:',
  '{{run.output}}',
  '',
  'Score how well the assistant answered the user, from 0 (wrong or unhelpful) to 1 (fully correct and helpful).',
].join('\n');

/** What a decision model reads when the check names no state of its own (the server's default too). */
export const DEFAULT_DECISION_STATE = {
  run: 'Input:\n{{run.input}}\n\nOutput:\n{{run.output}}',
  conversation: '{{conversation.transcript}}',
  span: 'Input:\n{{span.input}}\n\nOutput:\n{{span.output}}',
};

export const DEFAULT_DECISION_QUESTIONS = {
  answered: { type: 'noul', instructions: 'Does the output correctly answer the input?' },
};

/** A decision check's output: one enum per question, its outcomes. */
export function decisionOutput(questions) {
  const out = {};
  for (const [key, q] of Object.entries(questions || {})) {
    // A score is the model's number between levels (1.13 on 0–2): a number output, routed by ranges.
    out[key] = isRange(q) ? { type: 'number', min: 0, max: topOf(q) } : { type: 'enum', values: outcomesOf(q) };
  }
  return out;
}

export const NODE_KINDS = {
  python: {
    kind: 'python',
    type: 'guard',
    instrument: 'python',
    label: 'Python',
    blurb: 'Your own evaluate(t) — runs on every event, no model call.',
    Icon: Code2,
    accent: 'var(--accent-enterprise)',
    defaults: () => ({
      config: { source: DEFAULT_PYTHON_SOURCE },
      output: { ok: { type: 'boolean' } },
    }),
  },
  llm_judge: {
    kind: 'llm_judge',
    type: 'judgment',
    instrument: 'llm_judge',
    label: 'LLM judge',
    blurb: 'A model scores the trace against your prompt.',
    Icon: PiAtomDuotone,
    accent: 'var(--accent-llm)',
    defaults: () => ({
      config: { prompt: { user: DEFAULT_JUDGE_PROMPT }, model_params: { temperature: 0 } },
      output: { score: { type: 'number', min: 0, max: 1 } },
    }),
  },
  decision_model: {
    kind: 'decision_model',
    type: 'judgment',
    instrument: 'decision_model',
    label: 'Decision model',
    blurb: 'Typed questions answered by a decision model (Jev or Hartley) with probabilities.',
    Icon: TbArrowsSplit2,
    accent: 'var(--feedback-info)',
    defaults: (target = 'run') => {
      const questions = JSON.parse(JSON.stringify(DEFAULT_DECISION_QUESTIONS));
      return { config: { state: DEFAULT_DECISION_STATE[target] || DEFAULT_DECISION_STATE.run, questions, allow_abstain: true }, output: decisionOutput(questions) };
    },
  },
  ui_filter: {
    kind: 'ui_filter',
    type: 'guard',
    instrument: 'ui_filter',
    label: 'Filter',
    blurb: 'A rule on the trace fields. No code, no model.',
    Icon: Filter,
    accent: 'var(--text-secondary)',
    defaults: () => ({
      config: { filter: { all: [{ path: 'run.status', op: 'eq', value: 'ok' }] } },
      output: { matched: { type: 'boolean' } },
    }),
  },
  semantic: {
    kind: 'semantic',
    type: 'guard',
    instrument: 'cross_encoder',   // the default check; a node's own instrument is kept by kindOf
    label: 'Semantic check',
    blurb: 'True or false with no judge call: grounded, a statement, answered, repeated, or matches an example.',
    Icon: PiMagnifyingGlassDuotone,
    accent: 'var(--feedback-success)',
    defaults: (target) => defaultsFor('grounded', target),
  },
  human: {
    kind: 'human',
    type: 'human',
    instrument: 'human',
    label: 'Human review',
    blurb: 'Parks the run in the grading queue; a person picks an option.',
    Icon: PiUserCheckDuotone,
    accent: 'var(--feedback-warning)',
    defaults: () => ({
      config: { queue_config: { instructions: 'Is this answer acceptable?' } },
      output: { choice: { type: 'enum', values: ['yes', 'no'] } },
    }),
  },
  verdict: {
    kind: 'verdict',
    type: 'verdict',
    instrument: null,
    label: 'Verdict',
    blurb: 'Where the walk ends: pass or fail, with a reason.',
    Icon: PiSealCheckDuotone,
    accent: 'var(--feedback-success)',
    defaults: () => ({ config: { passed: true } }),
  },
};

/** The kinds a person can add from the palette, in the order they are shown. */
export const ADDABLE_KINDS = ['python', 'llm_judge', 'decision_model', 'ui_filter', 'semantic', 'human'];

/** Which kind a stored node is — by instrument, or verdict. */
export function kindOf(node) {
  if (!node) return null;
  if (node.type === 'verdict') return 'verdict';
  if (node.instrument === 'bi_encoder' || node.instrument === 'cross_encoder') return 'semantic';
  return node.instrument || null;
}

export function kindMeta(kindOrNode) {
  const k = typeof kindOrNode === 'string' ? kindOrNode : kindOf(kindOrNode);
  return NODE_KINDS[k] || { kind: k, label: k || 'node', Icon: Code2, accent: 'var(--text-secondary)', blurb: '' };
}

/** A verdict's mark depends on what it says. */
export function verdictIcon(node) {
  return node?.config?.passed === false ? PiXCircleDuotone : PiSealCheckDuotone;
}
export function verdictAccent(node) {
  return node?.config?.passed === false ? 'var(--feedback-error)' : 'var(--feedback-success)';
}
